import { useEffect, useState } from "react";

export type GeoState = {
  lat: number;
  lon: number;
  ready: boolean;
  permission: PermissionState | "unknown";
};

const BKK = { lat: 13.7563, lon: 100.5018 };

export function useUserLocation(): GeoState {
  const [state, setState] = useState<GeoState>({
    lat: BKK.lat,
    lon: BKK.lon,
    ready: false,
    permission: "unknown",
  });

  useEffect(() => {
    let cancelled = false;

    async function detectPermission() {
      if (typeof navigator === "undefined" || !navigator.permissions) return "unknown" as const;
      try {
        const p = await navigator.permissions.query({ name: "geolocation" as PermissionName });
        if (!cancelled) setState((s) => ({ ...s, permission: p.state }));
        p.onchange = () => !cancelled && setState((s) => ({ ...s, permission: p.state }));
        return p.state;
      } catch {
        return "unknown" as const;
      }
    }

    detectPermission();

    if (!navigator.geolocation) {
      setState((s) => ({ ...s, ready: true }));
      return () => {
        cancelled = true;
      };
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setState({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          ready: true,
          permission: "granted",
        });
      },
      () => {
        if (cancelled) return;
        setState((s) => ({ ...s, ready: true }));
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
    );

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
